import { useTransactionSummary } from '@mgonetwork/core';
import {
	type MgoTransactionBlockResponse,
	type ProgrammableTransaction,
} from '@mgonetwork/mango.js/client';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';

import { Signatures } from './Signatures';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useRecognizedPackages } from '~/hooks/useRecognizedPackages';
import { Events } from '~/pages/transaction-result/Events';
import { TransactionData } from '~/pages/transaction-result/TransactionData';
import { TransactionSummary } from '~/pages/transaction-result/transaction-summary';
import { TransactionDetailCard } from '~/pages/transaction-result/transaction-summary/TransactionDetailCard';
import { LOCAL_STORAGE_SPLIT_PANE_KEYS, SplitPanes } from '~/ui/SplitPanes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/Tabs';

import { GasBreakdown } from '~/components/GasBreakdown';
import { InputsCard } from '~/pages/transaction-result/programmable-transaction-view/InputsCard';
import { TransactionsCard } from '~/pages/transaction-result/programmable-transaction-view/TransactionsCard';

import styles from './TransactionResult.module.css';

function TabsContentContainer({ value, children }: { value: string; children: ReactNode }) {
	return (
		<TabsContent value={value}>
			<div className="mt-5">{children}</div>
		</TabsContent>
	);
}

export function TransactionView({ transaction }: { transaction: MgoTransactionBlockResponse }) {
	const isMediumOrAbove = useBreakpoint('md');
	const [isCollapsed, setIsCollapsed] = useState(false);

	const hasEvents = !!transaction.events?.length;

	const transactionKindName = transaction.transaction?.data.transaction?.kind;

	const isProgrammableTransaction = transactionKindName === 'ProgrammableTransaction';
	const recognizedPackagesList = useRecognizedPackages();
	const summary = useTransactionSummary({
		transaction,
		recognizedPackagesList,
	});
	const programmableTxn = transaction.transaction!.data.transaction as ProgrammableTransaction;
	const leftPane = {
		panel: (
			<>
				<TransactionDetailCard
					timestamp={summary?.timestamp}
					sender={summary?.sender}
					checkpoint={transaction.checkpoint}
					executedEpoch={transaction.effects?.executedEpoch}
				/>
				<div className="hidden-scroll mt-12 h-full overflow-y-auto border border-transparent md:h-full md:max-h-screen">
					<Tabs size="lg" defaultValue="summary">
						<TabsList>
							<TabsTrigger value="summary">Summary</TabsTrigger>
							{hasEvents && <TabsTrigger value="events">Events</TabsTrigger>}
							{isProgrammableTransaction && (
								<TabsTrigger value="signatures">Signatures</TabsTrigger>
							)}
							{isProgrammableTransaction && <TabsTrigger value="Inputs">Inputs</TabsTrigger>}
							{isProgrammableTransaction && (
								<TabsTrigger value="Transactions">Transactions</TabsTrigger>
							)}
							{isProgrammableTransaction && (
								<TabsTrigger value="Gas & Storage Fee">Gas & Storage Fee</TabsTrigger>
							)}
						</TabsList>
						<TabsContentContainer value="summary">
							<TransactionSummary transaction={transaction} />
						</TabsContentContainer>
						{hasEvents && (
							<TabsContentContainer value="events">
								<Events events={transaction.events!} />
							</TabsContentContainer>
						)}
						<TabsContentContainer value="signatures">
							<ErrorBoundary>
								<Signatures transaction={transaction} />
							</ErrorBoundary>
						</TabsContentContainer>
						<TabsContentContainer value="Inputs">
							<ErrorBoundary>
								<InputsCard inputs={programmableTxn.inputs} />
							</ErrorBoundary>
						</TabsContentContainer>
						<TabsContentContainer value="Transactions">
							<ErrorBoundary>
								<TransactionsCard transactions={programmableTxn.transactions} />
							</ErrorBoundary>
						</TabsContentContainer>
						<TabsContentContainer value="Gas & Storage Fee">
							<ErrorBoundary>
								<GasBreakdown summary={summary} />
							</ErrorBoundary>
						</TabsContentContainer>
					</Tabs>
				</div>
			</>
		),
		minSize: 35,
		collapsible: true,
		collapsibleButton: true,
		noHoverHidden: isMediumOrAbove,
	};

	const rightPane = {
		panel: (
			<div
				className={clsx(
					'h-full w-full overflow-y-auto md:overflow-y-hidden',
					isCollapsed && isMediumOrAbove && 'pl-2',
				)}
			>
				<TransactionData transaction={transaction} />
			</div>
		),
		minSize: 40,
		defaultSize: isProgrammableTransaction ? 65 : 50,
	};

	return (
		<div className={clsx(styles.txdetailsbg)}>
			<div className="h-screen md:h-full">
				<SplitPanes
					autoSaveId={LOCAL_STORAGE_SPLIT_PANE_KEYS.TRANSACTION_VIEW}
					onCollapse={setIsCollapsed}
					dividerSize={isMediumOrAbove ? 'md' : 'lg'}
					splitPanels={[leftPane]}
					// splitPanels={[leftPane, rightPane]}
					direction={isMediumOrAbove ? 'vertical' : 'horizontal'}
				/>
			</div>
		</div>
	);
}
